<template>
  <div class="game-board">
    <canvas ref="canvas"
      tabIndex="0"
      :autofocus="autofocus"
      class="game-canvas"
      @mousedown.prevent
      @mousemove="onInput"
      @mousewheel="onInput"
      @mouseout="onInput"
      @click.prevent="onInput"
      @contextmenu.prevent="onInput"
      @touchstart.prevent="onInput"
      @touchmove.prevent="onInput"
      @touchend.prevent="onInput"
      @focusout="onCanvasFocusOut"
      />
    <Renderer
        :canvas="canvas"
        :playMode="playMode"
        :state="state"
        :inputState="inputState"
        @uiSettings="onUISettings"
        />
  </div>
</template>

<script>
import State, { StoneChain, MarkChain, InfluenceChain, InputValidator } from './State';
import InputState from './InputState';
import Renderer from './Renderer';
import InputHandler, { Action } from './InputHandler';

export { State, StoneChain, MarkChain, InfluenceChain, InputValidator, Action };

export default {
  name: 'GameBoard',

  components: {
    Renderer,
  },

  props: {
    size: { type: Number, default: 700 },
    playMode: { type: String, default: null },
    state: { type: State, required: true },
    // Takes the keyboard when the page opens; off for a preview below other content, which the
    // browser would otherwise scroll to.
    autofocus: { type: Boolean, default: true },
  },

  watch: {
    'state.input.inputType': function () {
      this.inputState.reset();
    },

    playMode() {
      this.inputState.reset();
    },
  },

  data() {
    return {
      canvas: null,
      context: null,
      inputHandler: null,
      inputState: new InputState(),
    };
  },

  async mounted() {
    this.canvas = this.$refs['canvas'];
    this.context = this.canvas.getContext('2d');
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

    if(this.size > 0) {
      this.canvas.width = this.size;
      this.canvas.height = this.size;
    } else {
      this.canvas.width = this.canvas.parentElement.clientWidth - 2;
      this.canvas.height = this.canvas.width;
    }
  },

  beforeDestroy() { document.removeEventListener('pointerdown', this.onDocumentPointerDown, true); },

  methods: {
    onInput(e) {
      if(!this.inputHandler) return;

      if(e.type === 'click' || e.type == 'touchstart')
        this.canvas.focus();

      this.inputHandler.handle(e, a => {
        switch(a.type) {
          case Action.Type.CURSOR:
            if(this.state.input.inputType !== null)
              this.inputState.updateCursor(a.point);
            break;

          case Action.Type.PUT:
            if(this.state.input.inputType === null) {
              // No point (nothing is placed), but where the tap was: the replay opens a suggestion's line there.
              const put = new Action(Action.Type.PUT);
              put.at = a.point;
              this.$emit('action', put);
              break;
            }

            this.inputState.updateCursor(null);

            if(this.playMode === 'button') {
              if(this.inputState.updateSelected(a.point))
                this.$emit('action', new Action(Action.Type.SELECT, a.point));
            } else {
              this.$emit('action', a);
            }
            break;

          default:
            this.$emit('action', a);
        }
      });
    },

    clearSelection() {
      if(this.inputState.updateSelected(null))
        this.$emit('action', new Action(Action.Type.SELECT, null));
    },

    onDocumentPointerDown(e) {
      // A tap outside is explicit intent to leave the selection, even when that element cannot
      // take focus. A native permission dialog or iframe focus change is not an outside tap.
      if(!this.$el.contains(e.target) && !e.target.closest('.put, .placement'))
        this.clearSelection();
    },

    onCanvasFocusOut(e) {
      // Android WebViews and native WeChat controls can omit relatedTarget. Clearing here would
      // unmount the placement button before its click/subscription callback can play the stone.
      // Known keyboard focus destinations still clear selection; outside taps are handled above.
      if(e.relatedTarget && !e.relatedTarget.closest('.put, .placement'))
        this.clearSelection();
    },

    onUISettings(ui) {
      this.inputHandler = new InputHandler(this.state.grid, ui.px.start, ui.px.spacing);
    },
  },
}
</script>

<style scoped>
.game-canvas {
  border: solid 1px #aaa;
  outline: none;
}
</style>
